import { BookMarked, Gift, Home, Menu, MessageCircle, UserRound, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavButton from './NavButton';
import './styles/NavigationPanel.css';
import './styles/UtilsButton.css';

const iconSize = 28;
const HELP_BOT_URL = 'https://t.me/ogosa_help_bot';

// Константные данные для навигации (вынесены из компонента)
const NAV_ITEMS = [
    { key: 'home', title: 'домой', path: '/dashboard', icon: Home },
    { key: 'setup', title: 'настр.', path: '/setup', icon: BookMarked },
    { key: 'loyalty', title: 'бонусы', path: '/loyalty', icon: Gift },
    { key: 'account', title: 'акк.', path: '/account', icon: UserRound },
];

const BASE_DELAY = 60; // ms
const DELAY_STEP = 60; // ms increment per item
const SCROLL_CLOSE_THRESHOLD = 110;
const NAVIGATION_SCROLL_GRACE_PERIOD = 1800;

export type NavigationPanelProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const NavigationPanel: React.FC<NavigationPanelProps> = ({ open, onOpenChange }) => {
    const navigate = useNavigate();
    const panelRef = useRef<HTMLDivElement>(null);
    const scrollFrameRef = useRef<number | null>(null);
    const ignoreScrollUntilRef = useRef(0);

    useEffect(() => {
        if (!open) return;

        const scrollContainer = panelRef.current?.closest<HTMLElement>('.app-main-container');
        const scrollTarget: Window | HTMLElement = scrollContainer ?? window;
        const getScrollPosition = () => scrollContainer?.scrollTop ?? window.scrollY;
        const initialScrollPosition = getScrollPosition();
        const handleScroll = () => {
            if (scrollFrameRef.current !== null) return;

            scrollFrameRef.current = window.requestAnimationFrame(() => {
                scrollFrameRef.current = null;
                if (Date.now() < ignoreScrollUntilRef.current) return;
                if (Math.abs(getScrollPosition() - initialScrollPosition) >= SCROLL_CLOSE_THRESHOLD) {
                    onOpenChange(false);
                }
            });
        };

        scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            scrollTarget.removeEventListener('scroll', handleScroll);
            if (scrollFrameRef.current !== null) {
                window.cancelAnimationFrame(scrollFrameRef.current);
                scrollFrameRef.current = null;
            }
        };
    }, [open, onOpenChange]);

    const setOpen = useCallback((v: boolean) => {
        onOpenChange?.(v);
    }, [onOpenChange]);

    const handleNavigate = useCallback((to: string) => {
        ignoreScrollUntilRef.current = Date.now() + NAVIGATION_SCROLL_GRACE_PERIOD;
        navigate(to);
    }, [navigate]);

    const handleOpenHelp = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.openTelegramLink(HELP_BOT_URL);
        } else {
            window.open(HELP_BOT_URL, '_blank');
        }
    }, []);

    const navButtons = useMemo(() => (
        <>
            {NAV_ITEMS.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                    <NavButton
                        key={item.key}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleNavigate(item.path);
                        }}
                        className="util-button stagger-item"
                        delay={BASE_DELAY + idx * DELAY_STEP}
                        aria-label={item.title}
                    >
                        <IconComponent size={iconSize} />
                        {/* <span className="nav-label">{item.title}</span> */}
                    </NavButton>
                );
            })}
            <NavButton
                key="help"
                onClick={(event) => {
                    event.stopPropagation();
                    handleOpenHelp();
                }}
                className="util-button stagger-item"
                delay={BASE_DELAY + NAV_ITEMS.length * DELAY_STEP}
                aria-label="помощь"
            >
                <MessageCircle size={iconSize} />
                {/* <span className="nav-label">помощь</span> */}
            </NavButton>
        </>
    ), [handleNavigate, handleOpenHelp]);

    return (
        <div ref={panelRef} className={`nav-panel ${open ? 'open' : 'collapsed'}`} aria-hidden={false}>
            <button
                className="glass-ui util-button"
                aria-label={open ? 'Свернуть панель' : 'Открыть панель'}
                onClick={() => setOpen(!open)}
            >
                {open ? <X size={iconSize} /> : <Menu size={iconSize} />}
            </button>
            <div className="nav-buttons glass-ui">
                {navButtons}
            </div>
        </div>
    );
};

export default NavigationPanel;
